import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useModulePermissions } from "../../hooks/useNavigationPermissions";
import {
  NavigationItemConfig,
  NestedGroupConfig,
  getVisibleNestedItems,
  isSuperAdmin,
  getUserModules,
} from "../../utils/navigationUtils";

interface NestedPageProps {
  group: NestedGroupConfig;
}

const SidebarItem = ({
  icon: Icon,
  label,
  active,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  active: boolean;
  onClick: () => void;
}) => (
  <div
    className={`flex items-center justify-center w-12 h-12 my-2 rounded-full cursor-pointer transition-colors ${
      active
        ? "bg-primary-default text-white"
        : "text-gray-500 hover:bg-gray-200"
    }`}
    onClick={onClick}
    title={label}
  >
    <Icon className="h-6 w-6" />
  </div>
);

const NestedPage: React.FC<NestedPageProps> = ({ group }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selected, setSelected] = useState<string>("");
  const { profile } = useAuth();

  const isSuperAdminUser = useMemo(
    () => isSuperAdmin(profile?.role),
    [profile?.role]
  );

  const userModules = useMemo(() => {
    const permissions: string[] =
      profile && Array.isArray(profile.permissions) ? profile.permissions : [];
    return getUserModules(permissions);
  }, [profile]);

  // Get unique modules for this group
  const uniqueModules = useMemo(() => {
    return Array.from(new Set(group.items.map((item) => item.module)));
  }, [group.items]);

  // Get permissions for all modules in this group
  const modulePermissions = useModulePermissions(uniqueModules);

  // Get visible items based on permissions
  const filteredVisibleItems = useMemo(() => {
    const items = getVisibleNestedItems(group, userModules, isSuperAdminUser);
    return items.filter((item) => {
      // Check if user has view permission for this module
      return modulePermissions[item.module] ?? false;
    });
  }, [group, userModules, isSuperAdminUser, modulePermissions]);

  // Set initial selected based on current route
  useEffect(() => {
    const currentItem = group.items.find((item) =>
      location.pathname.startsWith(item.route)
    );
    if (
      currentItem &&
      filteredVisibleItems.some((item) => item.id === currentItem.id)
    ) {
      setSelected(currentItem.id);
    } else if (filteredVisibleItems.length > 0) {
      navigate(filteredVisibleItems[0].route, { replace: true });
    }
  }, [location.pathname, filteredVisibleItems, navigate, group.items]);

  if (filteredVisibleItems.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">
          You don't have permission to access this page.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-[90vh]">
      {/* Sidebar */}
      <div className="w-16 bg-white border-r flex flex-col items-center py-4 md:h-full h-auto justify-center">
        {filteredVisibleItems.map((item) => {
          const Icon = item.icon || (() => null);
          return (
            <SidebarItem
              key={item.id}
              icon={Icon}
              label={item.name}
              active={selected === item.id}
              onClick={() => {
                setSelected(item.id);
                navigate(item.route);
              }}
            />
          );
        })}
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
};

export default NestedPage;
